import { type ReactNode, useMemo } from "react";
import styled from "styled-components";

import type { FloorSpaceCatalog } from "@/models/floor-space-catalog";
import type Light from "@/models/light";
import {
    getFirmwareColor,
    parseMajorVersion,
    UNKNOWN_COLOR,
} from "@/utils/map/firmware-colors";

interface FirmwareLegendProps {
    /**
     * The lights currently displayed on the map.
     */
    lights: Light[];
    /**
     * The FloorSpaceCatalog lookup map keyed by floorSpaceCatalogID.
     */
    floorSpaceCatalogMap: Record<number, FloorSpaceCatalog>;
}

/**
 * Legend overlay for firmware view mode, showing firmware version to color mappings.
 *
 * @param {FirmwareLegendProps} props - The component props.
 * @returns {ReactNode} The rendered firmware legend.
 */
const FirmwareLegend = ({
    lights,
    floorSpaceCatalogMap,
}: FirmwareLegendProps): ReactNode => {
    const entries = useEntries(lights, floorSpaceCatalogMap);

    if (entries.length === 0) return null;

    return (
        <Container>
            <Title>Firmware Version</Title>
            {entries.map(({ label, color }) => (
                <Entry key={label}>
                    <ColorSwatch $color={color} />
                    <span>{label}</span>
                </Entry>
            ))}
        </Container>
    );
};

const useEntries = (
    lights: Light[],
    floorSpaceCatalogMap: Record<number, FloorSpaceCatalog>,
) =>
    useMemo(() => {
        const versions = new Set<string>();
        let hasUnknown = false;

        for (const light of lights) {
            const catalogId = light.options?.floorSpaceCatalog_ID;
            const catalog = catalogId ? floorSpaceCatalogMap[catalogId] : null;
            const version = catalog?.firmwareVersion;

            if (version) {
                versions.add(version);
            } else {
                hasUnknown = true;
            }
        }

        const sorted = Array.from(versions).sort((a, b) => {
            const majorA = parseMajorVersion(a) ?? Infinity;
            const majorB = parseMajorVersion(b) ?? Infinity;

            if (majorA !== majorB) return majorA - majorB;

            return a.localeCompare(b, undefined, { numeric: true });
        });

        const result = sorted.map((version) => ({
            label: version,
            color: getFirmwareColor(version),
        }));

        if (hasUnknown) {
            result.push({ label: "Unknown", color: UNKNOWN_COLOR });
        }

        return result;
    }, [lights, floorSpaceCatalogMap]);

export default FirmwareLegend;

const Container = styled.div`
    position: absolute;
    top: 80px;
    right: 10px;
    z-index: 1000;
    background-color: rgba(255, 255, 255, 0.92);
    border-radius: 6px;
    padding: 8px 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    max-height: 50vh;
    overflow-y: auto;
    font-size: 12px;
`;

const Title = styled.div`
    font-weight: 600;
    margin-bottom: 6px;
    border-bottom: 1px solid #ddd;
    padding-bottom: 4px;
`;

const Entry = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 0;
`;

interface ColorSwatchProps {
    $color: string;
}

const ColorSwatch = styled.div<ColorSwatchProps>`
    width: 12px;
    height: 12px;
    border-radius: 2px;
    background-color: ${({ $color }) => $color};
    flex-shrink: 0;
`;
